import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import WiseCheck from './components/WiseCheck';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import DataSecurity from './components/DataSecurity';
import NotFound from './components/NotFound';
import Packages from "./components/Packages";
// Dashboard imports disabled - portals need more work
// import Login from './components/Login';
// import Register from './components/Register';
// import ClientDashboard from './components/ClientDashboard';
// import AdminDashboard from './components/AdminDashboard';
import SEO from './components/SEO';
import CookieConsent from './components/CookieConsent';
import FAQ from './components/FAQ';
import Testimonials from './components/Testimonials';
import ContactForm from './components/ContactForm';
import Newsletter from './components/Newsletter';
import MobileNav from './components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// --- GA4 helpers (safe no-op if gtag isn't loaded yet) ---
const gaTrackEvent = (eventName, params = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (_) {
    // no-op
  }
};

const gaTrackPageView = (path) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: path });
    }
  } catch (_) {
    // no-op
  }
};

const SiteLayout = ({ children }) => {
  const location = useLocation();
  const lastEventAtRef = useRef({});

  const dedupeEvent = useCallback((key, windowMs = 3000) => {
    const now = Date.now();
    const last = lastEventAtRef.current[key] || 0;
    if (now - last < windowMs) return false;
    lastEventAtRef.current[key] = now;
    return true;
  }, []);
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If we're not on the homepage, go there and let the browser jump to the anchor
      window.location.href = `/#${sectionId}`;
    }
  };

  const navigateToWiseCheck = () => {
    window.location.href = '/wise-check';
  };

  // Track page views + Wise Check milestones by route
  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    gaTrackPageView(path);

    // Wise Check started (arrive on /wise-check)
    if (location.pathname === '/wise-check') {
      if (dedupeEvent('wise_check_started')) {
        gaTrackEvent('wise_check_started');
      }
    }

    // Wise Check completed (support common completion routes)
    if (
      location.pathname === '/wise-check/results' ||
      location.pathname === '/wise-check/result' ||
      location.pathname === '/wise-check/complete' ||
      location.pathname === '/wise-check/completed'
    ) {
      if (dedupeEvent('wise_check_completed')) {
        gaTrackEvent('wise_check_completed');
      }
    }
  }, [location.pathname, location.search, dedupeEvent]);

  // Global click tracking for form submits (uses data-testid selectors)
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target;
      if (!el) return;

      const bookingBtn = el.closest?.('[data-testid="booking-submit-button"]');
      if (bookingBtn && dedupeEvent('booking_completed')) {
        gaTrackEvent('booking_completed');
        return;
      }

      const contactBtn = el.closest?.('[data-testid="contact-submit"]');
      if (contactBtn && dedupeEvent('contact_submitted')) {
        gaTrackEvent('contact_submitted');
        return;
      }

      const newsletterBtn = el.closest?.('[data-testid="newsletter-submit"]');
      if (newsletterBtn && dedupeEvent('newsletter_subscribed')) {
        gaTrackEvent('newsletter_subscribed');
        return;
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dedupeEvent]);

  return (
    <>
      <nav className="fixed w-full bg-navy bg-opacity-95 backdrop-blur-sm z-50 border-b border-teal-500/20" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0" data-testid="logo-container">
              <Link to="/">
                <img src="/logo.png" alt="Mediawise Solutions" className="h-12 w-auto" />
              </Link>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <button onClick={() => scrollToSection('about')} className="nav-link" data-testid="nav-about-link">About</button>
              <button onClick={() => scrollToSection('services')} className="nav-link" data-testid="nav-services-link">Services</button>
              <Link to="/packages" className="nav-link" data-testid="nav-packages-link">Packages</Link>
              <Link to="/wise-check" className="nav-link" data-testid="nav-wise-check-link">Wise Check</Link>
            </div>
            <MobileNav
              scrollToSection={scrollToSection}
              setShowBookingModal={() => {}}
              navigateToWiseCheck={navigateToWiseCheck}
            />
          </div>
        </div>
      </nav>

      <div className="pt-24">
        {children}
      </div>
    </>
  );
};

const HomePage = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [bookingStatus, setBookingStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log(response.data.message);
      } catch (e) {
        console.error('Backend connection error:', e);
      }
    };
    checkBackend();
  }, []);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setBookingStatus('');

    try {
      await axios.post(`${API}/booking`, bookingForm);
      setBookingStatus('success');
      setBookingForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => {
        setShowBookingModal(false);
        setBookingStatus('');
      }, 2000);
    } catch (error) {
      setBookingStatus('error');
      console.error('Booking error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToWiseCheck = () => {
    window.location.href = '/wise-check';
  };

  return (
    <div className="App" data-testid="app-container">
      <SEO />

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h1 className="hero-title" data-testid="hero-title">
            Wiser solutions for <span className="gradient-text">smarter business</span>
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Mediawise Solutions helps regional Australian businesses grow through strategy, systems, and smarter marketing.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12" data-testid="hero-cta-buttons">
            <button 
              onClick={() => setShowBookingModal(true)} 
              className="cta-button-large"
              data-testid="hero-book-button"
            >
              Book a Strategy Session
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className="cta-button-secondary"
              data-testid="hero-services-button"
            >
              Explore Our Services
            </button>
            <button 
              onClick={navigateToWiseCheck} 
              className="cta-button-secondary"
              data-testid="hero-wise-check-button"
            >
              Complete the 'Wise Check'
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="section-title" data-testid="about-title">The Wise Framework</h2>
            <div className="gradient-line mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6" data-testid="about-content">
              <p className="text-lg text-gray-700 leading-relaxed">
                At Mediawise Solutions, we combine strategic marketing with streamlined business systems to help you work smarter, not harder.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our approach is grounded in clarity, efficiency, and measurable growth—so your business can scale confidently and sustainably.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We're not another marketing agency. We're a strategic partner that helps you see the bigger picture—aligning your brand, workflows, and customer experience so your business runs smarter and grows stronger.
              </p>
            </div>
            <div className="gradient-card" data-testid="framework-card">
              <h3 className="text-2xl font-bold text-navy mb-4">What We Do</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="gradient-icon">✓</span>
                  <span className="text-gray-700">Strategic marketing that aligns with your goals</span>
                </li>
                <li className="flex items-start">
                  <span className="gradient-icon">✓</span>
                  <span className="text-gray-700">Streamlined business systems for efficiency</span>
                </li>
                <li className="flex items-start">
                  <span className="gradient-icon">✓</span>
                  <span className="text-gray-700">Measurable growth and sustainable scaling</span>
                </li>
                <li className="flex items-start">
                  <span className="gradient-icon">✓</span>
                  <span className="text-gray-700">Customer experience optimization</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-dark" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="section-title-light" data-testid="services-title">Our Services</h2>
            <div className="gradient-line mx-auto"></div>
            <p className="text-cream/80 text-lg mt-6 max-w-3xl mx-auto">
              Comprehensive solutions designed to transform your regional business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-testid="services-grid">
            <div className="service-card" data-testid="service-card-strategy">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-navy mb-3">Business Strategy</h3>
              <p className="text-gray-600">
                Clear roadmaps and strategic planning to align your business goals with actionable steps.
              </p>
            </div>
            <div className="service-card" data-testid="service-card-marketing">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-bold text-navy mb-3">Smart Marketing</h3>
              <p className="text-gray-600">
                Data-driven marketing strategies that connect with your audience and drive real results.
              </p>
            </div>
            <div className="service-card" data-testid="service-card-systems">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-xl font-bold text-navy mb-3">Business Systems</h3>
              <p className="text-gray-600">
                Streamlined workflows and automated processes to help your business run efficiently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <FAQ />

      {/* Contact Form */}
      <ContactForm />

      {/* Newsletter */}
      <Newsletter />

      {/* Client Portal CTA - Disabled for now */}

      {/* Acknowledgement Section */}
      <section className="section-acknowledgement" data-testid="acknowledgement-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-gray-700 leading-relaxed text-sm">
            Mediawise Solutions acknowledges the Traditional Owners of the lands across Central Queensland on which we live, work, and create—including the Darumbal, Gangulu, and Woppaburra peoples—and pays our respects to Elders past, present, and emerging. We recognise their enduring connection to Country, culture, and community, and we honour their contributions to the region's rich story.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="section-footer" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-cream font-bold mb-4">Contact</h4>
              <p className="text-cream" data-testid="footer-email">mitch@mediawisesolutions.com.au</p>
              <p className="text-cream" data-testid="footer-phone">0409 265 711</p>
            </div>
            <div>
              <h4 className="text-cream font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-cream hover:text-teal-400 transition">About</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-cream hover:text-teal-400 transition">Services</button></li>
                <li><Link to="/packages" className="text-cream hover:text-teal-400 transition">Packages</Link></li>
                {/* Client Portal link disabled for now */}
                <li><button onClick={() => setShowBookingModal(true)} className="text-cream hover:text-teal-400 transition" data-testid="footer-book-link">Book a Session</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-cream font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy-policy" className="text-cream hover:text-teal-400 transition">Privacy Policy</a></li>
                <li><a href="/terms" className="text-cream hover:text-teal-400 transition">Terms of Use</a></li>
                <li><a href="/data-security" className="text-cream hover:text-teal-400 transition">Data Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-cream font-bold mb-4">Connect</h4>
              <div className="flex gap-3">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-cream/20 pt-8 text-center">
            <p className="text-cream text-sm">© 2025 Mediawise Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)} data-testid="booking-modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <button 
              onClick={() => setShowBookingModal(false)} 
              className="modal-close"
              data-testid="booking-modal-close"
            >
              ×
            </button>
            <h2 className="text-3xl font-bold text-navy mb-2" data-testid="booking-modal-title">Book Your Strategy Session</h2>
            <p className="text-gray-600 mb-6">Let's discuss how we can help your business grow smarter.</p>
            
            {bookingStatus === 'success' && (
              <div className="alert-success" data-testid="booking-success-message">
                ✓ Thank you! We'll be in touch soon.
              </div>
            )}
            
            {bookingStatus === 'error' && (
              <div className="alert-error" data-testid="booking-error-message">
                ✗ Something went wrong. Please try again or contact us directly.
              </div>
            )}
            
            <form onSubmit={handleBookingSubmit} className="space-y-4" data-testid="booking-form">
              <div>
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                  data-testid="booking-input-name"
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  required
                  className="form-input"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                  data-testid="booking-input-email"
                />
              </div>
              <div>
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  required
                  className="form-input"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                  data-testid="booking-input-phone"
                />
              </div>
              <div>
                <label className="form-label">Message</label>
                <textarea
                  className="form-input"
                  rows="4"
                  value={bookingForm.message}
                  onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                  placeholder="Tell us about your business and what you're looking to achieve..."
                  data-testid="booking-input-message"
                ></textarea>
              </div>
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="cta-button-large w-full"
                data-testid="booking-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Submit Booking Request'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <CookieConsent />
          <Routes>
            <Route path="/" element={<SiteLayout><HomePage /></SiteLayout>} />
            <Route path="/wise-check" element={<SiteLayout><WiseCheck /></SiteLayout>} />
            <Route path="/packages" element={<SiteLayout><Packages /></SiteLayout>} />
            <Route path="/privacy-policy" element={<SiteLayout><PrivacyPolicy /></SiteLayout>} />
            <Route path="/terms" element={<SiteLayout><TermsOfUse /></SiteLayout>} />
            <Route path="/data-security" element={<SiteLayout><DataSecurity /></SiteLayout>} />
            <Route path="*" element={<SiteLayout><NotFound /></SiteLayout>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;